interface PasswordStrengthIndicatorProps {
  password: string;
  isVisible: boolean;
}

interface PasswordRequirement {
  id: string;
  label: string;
  test: (password: string) => boolean;
}

const requirements: PasswordRequirement[] = [
  {
    id: 'length',
    label: 'At least 8 characters',
    test: (password) => password.length >= 8
  },
  {
    id: 'uppercase',
    label: 'At least one uppercase letter',
    test: (password) => /[A-Z]/.test(password)
  },
  {
    id: 'lowercase',
    label: 'At least one lowercase letter',
    test: (password) => /[a-z]/.test(password)
  },
  {
    id: 'number',
    label: 'At least one number',
    test: (password) => /\d/.test(password)
  },
  {
    id: 'special',
    label: 'At least one special character (@$!%*?&)',
    test: (password) => /[@$!%*?&]/.test(password)
  }
];

export function PasswordStrengthIndicator({ password, isVisible }: PasswordStrengthIndicatorProps) {
  if (!isVisible) return null;

  const metRequirements = requirements.filter(req => req.test(password));
  const strengthPercentage = (metRequirements.length / requirements.length) * 100;

  const getStrengthColor = () => {
    if (strengthPercentage < 40) return 'bg-red-500';
    if (strengthPercentage < 80) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getStrengthLabel = () => {
    if (strengthPercentage < 40) return 'Weak';
    if (strengthPercentage < 80) return 'Good';
    return 'Strong';
  };

  return (
    <div className="mt-2">
      {password.length > 0 && (
        <div className="mb-3">
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm text-muted-foreground">Password strength:</span>
            <span className={`text-sm font-medium ${
              strengthPercentage < 40 ? 'text-red-600' :
              strengthPercentage < 80 ? 'text-yellow-600' : 'text-green-600'
            }`}>
              {getStrengthLabel()}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className={`h-2 rounded-full transition-all duration-300 ${getStrengthColor()}`}
              style={{ width: `${strengthPercentage}%` }}
            />
          </div>
        </div>
      )}

      <div className="space-y-1">
        <p className="text-xs text-muted-foreground mb-2">Password must have:</p>
        {requirements.map((req) => {
          const isMet = req.test(password);
          return (
            <div key={req.id} className="flex items-center text-xs">
              <span className={`mr-2 ${isMet ? 'text-green-600' : 'text-gray-400'}`}>
                {isMet ? '✓' : '○'}
              </span>
              <span className={isMet ? 'text-green-600' : 'text-muted-foreground'}>
                {req.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
} 